import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { Folder, FileText, Settings, Trash2, Plus, FolderPlus } from 'lucide-react';
import './Atrankos.scss';

interface SelectionFolder {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  selections_count: number;
}

interface Selection {
  id: number;
  user_id: number;
  folder_id?: number;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  candidates_count: number;
  folder_name?: string;
}

/**
 * Atrankos (Selections) Page
 * 
 * Employer-only page for organizing candidate CVs into folders and selections.
 * Allows creating folders (aplankai) and selections (atrankos), and organizing candidates.
 */
const Atrankos: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState<SelectionFolder[]>([]);
  const [selections, setSelections] = useState<Selection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState<SelectionFolder | null>(null);
  const [editingSelection, setEditingSelection] = useState<Selection | null>(null);
  const [filterBy, setFilterBy] = useState<'all' | 'folders' | 'selections'>('all');

  // Form states
  const [folderName, setFolderName] = useState('');
  const [folderDescription, setFolderDescription] = useState('');
  const [selectionName, setSelectionName] = useState('');
  const [selectionDescription, setSelectionDescription] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employer') {
      fetchFolders();
      fetchSelections();
    }
  }, [isAuthenticated, user]);

  const fetchFolders = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/folders`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setFolders(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchSelections = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/selections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        setSelections(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching selections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = () => {
    setEditingFolder(null);
    setFolderName('');
    setFolderDescription('');
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder: SelectionFolder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderDescription(folder.description || '');
    setShowFolderModal(true);
  };

  const handleSaveFolder = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const url = editingFolder
        ? `${API_URL}/employer-selections/folders/${editingFolder.id}`
        : `${API_URL}/employer-selections/folders`;
      
      const method = editingFolder ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: folderName,
          description: folderDescription
        })
      });

      if (response.ok) {
        setShowFolderModal(false);
        fetchFolders();
      }
    } catch (error) {
      console.error('Error saving folder:', error);
    }
  };

  const handleDeleteFolder = async (folderId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šį aplanką?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/folders/${folderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchFolders();
        fetchSelections(); // Refresh selections as they might be affected
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };

  const handleCreateSelection = () => {
    setEditingSelection(null);
    setSelectionName('');
    setSelectionDescription('');
    setSelectedFolderId(null);
    setShowSelectionModal(true);
  };

  const handleEditSelection = (selection: Selection) => {
    setEditingSelection(selection);
    setSelectionName(selection.name);
    setSelectionDescription(selection.description || '');
    setSelectedFolderId(selection.folder_id || null);
    setShowSelectionModal(true);
  };

  const handleSaveSelection = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const url = editingSelection
        ? `${API_URL}/employer-selections/selections/${editingSelection.id}`
        : `${API_URL}/employer-selections/selections`;
      
      const method = editingSelection ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: selectionName,
          description: selectionDescription,
          folder_id: selectedFolderId
        })
      });

      if (response.ok) {
        setShowSelectionModal(false);
        fetchSelections();
      }
    } catch (error) {
      console.error('Error saving selection:', error);
    }
  };

  const handleDeleteSelection = async (selectionId: number) => {
    if (!window.confirm('Ar tikrai norite ištrinti šią atranką?')) return;

    try {
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/employer-selections/selections/${selectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchSelections();
      }
    } catch (error) {
      console.error('Error deleting selection:', error);
    }
  };

  const handleOpenFolder = (folderId: number) => {
    navigate(`/atrankos/folder/${folderId}`);
  };

  const handleOpenSelection = (selectionId: number) => {
    navigate(`/atrankos/selection/${selectionId}`);
  };

  // Filter logic
  const getFilteredItems = () => {
    const items: (SelectionFolder | Selection)[] = [];
    
    if (filterBy === 'all' || filterBy === 'folders') {
      items.push(...folders);
    }
    
    if (filterBy === 'all' || filterBy === 'selections') {
      items.push(...selections.filter(s => !s.folder_id)); // Only standalone selections
    }
    
    return items;
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="atrankos-container">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect non-employers
  if (!isAuthenticated || user?.user_type !== 'employer') {
    return <Navigate to="/" />;
  }

  return (
    <div className="atrankos-page">
      <div className="container">
        <div className="page-header">
          <h1>Atrankos</h1>
          <p className="subtitle">Organizuokite kandidatus į aplankus ir atrankos</p>
        </div>

        <div className="filters-section">
          <div className="filter-buttons">
            <button 
              className={filterBy === 'all' ? 'active' : ''}
              onClick={() => setFilterBy('all')}
            >
              Rodyti visas
            </button>
            <button 
              className={filterBy === 'folders' ? 'active' : ''}
              onClick={() => setFilterBy('folders')}
            >
              Rodyti aplankus
            </button>
            <button 
              className={filterBy === 'selections' ? 'active' : ''}
              onClick={() => setFilterBy('selections')}
            >
              Nerodyti aplankų
            </button>
          </div>

          <div className="action-buttons">
            <button className="btn-create-selection" onClick={handleCreateSelection}>
              <Plus size={20} />
              Nauja atranka
            </button>
            <button className="btn-create-folder" onClick={handleCreateFolder}>
              <FolderPlus size={20} />
              Naujas atrankų aplankas
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Kraunama...</div>
        ) : getFilteredItems().length === 0 ? (
          <div className="empty-state">
            <Folder size={64} />
            <p>Dar neturite sukurtų atranką</p>
            <p className="hint">Sukurkite naują atranką arba aplanką, kad pradėtumėte organizuoti kandidatus</p>
          </div>
        ) : (
          <div className="items-grid">
            {getFilteredItems().map((item) => {
              const isFolder = 'selections_count' in item;
              const folder = isFolder ? item as SelectionFolder : null;
              const selection = !isFolder ? item as Selection : null;

              return (
                <div key={`${isFolder ? 'folder' : 'selection'}-${item.id}`} className="item-card">
                  <div className="card-header">
                    <div className="card-icon">
                      {isFolder ? <Folder size={48} /> : <FileText size={48} />}
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-icon"
                        onClick={() => isFolder ? handleEditFolder(folder!) : handleEditSelection(selection!)}
                      >
                        <Settings size={18} />
                      </button>
                      <button 
                        className="btn-icon btn-delete"
                        onClick={() => isFolder ? handleDeleteFolder(item.id!) : handleDeleteSelection(item.id!)}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div className="card-body">
                    <h3 className="card-title">{item.name}</h3>
                    {item.description && <p className="card-description">{item.description}</p>}
                    <div className="card-info">
                      {isFolder ? (
                        <span>{folder!.selections_count} atrankos</span>
                      ) : (
                        <span>{selection!.candidates_count} kandidatai</span>
                      )}
                    </div>
                  </div>

                  <button 
                    className="btn-open"
                    onClick={() => isFolder ? handleOpenFolder(item.id!) : handleOpenSelection(item.id!)}
                  >
                    ATIDARYTI
                    <span className="arrow">→</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Create/Edit Folder Modal */}
        {showFolderModal && (
          <div className="modal-overlay" onClick={() => setShowFolderModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingFolder ? 'Redaguoti aplanką' : 'Naujas aplankas'}</h2>
                <button className="btn-close" onClick={() => setShowFolderModal(false)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="folderName">Pavadinimas *</label>
                  <input
                    type="text"
                    id="folderName"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="pvz., IT Specialistai"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="folderDescription">Aprašymas</label>
                  <textarea
                    id="folderDescription"
                    value={folderDescription}
                    onChange={(e) => setFolderDescription(e.target.value)}
                    placeholder="Trumpas aprašymas apie šį aplanką"
                    rows={3}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowFolderModal(false)}>
                  Atšaukti
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSaveFolder}
                  disabled={!folderName.trim()}
                >
                  {editingFolder ? 'Atnaujinti' : 'Sukurti'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Selection Modal */}
        {showSelectionModal && (
          <div className="modal-overlay" onClick={() => setShowSelectionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingSelection ? 'Redaguoti atranką' : 'Nauja atranka'}</h2>
                <button className="btn-close" onClick={() => setShowSelectionModal(false)}>&times;</button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="selectionName">Pavadinimas *</label>
                  <input
                    type="text"
                    id="selectionName"
                    value={selectionName}
                    onChange={(e) => setSelectionName(e.target.value)}
                    placeholder="pvz., Frontend Developer pozicija"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="selectionDescription">Aprašymas</label>
                  <textarea
                    id="selectionDescription"
                    value={selectionDescription}
                    onChange={(e) => setSelectionDescription(e.target.value)}
                    placeholder="Trumpas aprašymas apie šią atranką"
                    rows={3}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="selectionFolder">Aplankas (nebūtina)</label>
                  <select
                    id="selectionFolder"
                    value={selectedFolderId || ''}
                    onChange={(e) => setSelectedFolderId(e.target.value ? parseInt(e.target.value) : null)}
                  >
                    <option value="">Be aplanko</option>
                    {folders.map(folder => (
                      <option key={folder.id} value={folder.id}>{folder.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowSelectionModal(false)}>
                  Atšaukti
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSaveSelection}
                  disabled={!selectionName.trim()}
                >
                  {editingSelection ? 'Atnaujinti' : 'Sukurti'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Atrankos;

